import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Metadata from '../components/Metadata';
import InfiniteSlider from '../components/InfiniteSlider';
import Lightbox from '../components/Lightbox';
import { fetchGallery, fetchReviews } from '../lib/cms';
import { services } from '../data/services';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Forside() {
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      const [galleryData, reviewsData] = await Promise.all([
        fetchGallery(),
        fetchReviews()
      ]);
      setGallery(galleryData);
      setReviews(reviewsData);
    }
    loadData();
  }, []);

  // Get top 3 projects for selected references
  const selectedProjects = gallery.slice(0, 3);

  return (
    <>
      <Metadata
        title="K. Pheiffer Construction"
        description="K. Pheiffer Construction udfører kvalitetsbyggeri, renovering og håndværksopgaver med fokus på godt håndværk og flotte resultater."
      />

      {/* Hero Section */}
      <section className="relative bg-slate-950 text-white overflow-hidden py-24 sm:py-32 lg:py-40">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <video
            className="h-full w-full object-cover opacity-35 pointer-events-none select-none"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/cf131c5c-877c-4f32-8100-a69e4d795732/danish-house-renovation.jpg"
          >
            <source src="https://assets.auxodev.dk/assets/cf131c5c-877c-4f32-8100-a69e4d795732/hero-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/35 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold leading-5 text-emerald-400 border border-emerald-500/20 mb-6 uppercase tracking-wider">
              <i className="fa-solid fa-circle text-[6px] animate-pulse" /> Professionelt tømrerarbejde
            </span>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl leading-tight">
              Kvalitetshåndværk med fokus på detaljen
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
              K. Pheiffer Construction hjælper med bygge- og renoveringsopgaver, hvor kvalitet, ærlighed og godt håndværk er i centrum.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3.5 text-base font-semibold tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-emerald-900/20 active:scale-95"
              >
                Få et tilbud
              </Link>
              <Link
                to="/galleri"
                className="inline-flex items-center justify-center rounded border border-slate-700 hover:border-white text-white hover:bg-white/5 px-6 py-3.5 text-base font-semibold tracking-wide transition-colors duration-200"
              >
                Se referencer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image Column */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-4 rounded-2xl bg-slate-100 -z-10" />
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/cf131c5c-877c-4f32-8100-a69e4d795732/spa-rkonstruktion.jpg"
                alt="Spærkonstruktion under opførelse"
                className="rounded-xl shadow-lg w-full object-cover aspect-[4/3] sm:aspect-[3/2] lg:aspect-square"
              />
              <div className="absolute -bottom-6 -right-6 bg-slate-950 text-white p-6 rounded-lg hidden sm:block border border-slate-800">
                <p className="text-3xl font-extrabold text-emerald-400">100%</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mt-1 font-medium">Dansk kvalitet</p>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                Hvem er vi?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Pålidelig tømrer i Fredericia
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                K. Pheiffer Construction er din lokale samarbejdspartner, når det kommer til professionelt tømrerarbejde. Vi har base i Fredericia og dækker hele Trekantområdet. Vi lægger stor vægt på ærlighed, punktlighed og kompromisløs kvalitet i alt vores arbejde.
              </p>
              <p className="text-slate-600 text-base leading-relaxed">
                Uanset om du skal have lagt nyt tag, bygget en lækker træterrasse eller renoveret facaden, står vi klar til at hjælpe dig sikkert i mål med dit projekt. Vi rådgiver dig hele vejen fra idé til færdigt resultat, så du får en løsning, der holder i mange år.
              </p>
              <div className="pt-4">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-emerald-600 transition-colors group"
                >
                  Læs mere om vores værdier
                  <i className="fa-regular fa-arrow-right transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jeg tilbyder Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Hvad tilbyder vi?
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
              Professionelle tømrerløsninger
            </h2>
            <p className="mt-4 text-slate-500 text-base sm:text-lg">
              Vi udfører en bred vifte af bygge- og renoveringsopgaver for private og erhverv med fokus på holdbarhed og flot finish.
            </p>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service, index) => (
              <Link
                key={service.id}
                to={`/jeg-tilbyder/${service.id}`}
                className={`group flex flex-col bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${
                  index === 2 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={getOptimizedImageUrl(service.image, 800, 90)}
                    alt={service.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 h-10 w-10 rounded-lg bg-slate-950 text-white flex items-center justify-center border border-slate-800">
                    <i className={`${service.icon} text-lg text-emerald-400`} />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-slate-500 text-sm leading-relaxed flex-grow">
                    {service.shortDescription}
                  </p>
                  <div className="mt-6 pt-4 border-t border-slate-50 flex items-center justify-between text-xs font-bold text-slate-900 uppercase tracking-wider">
                    <span>Læs mere</span>
                    <i className="fa-regular fa-arrow-right text-emerald-600 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/jeg-tilbyder"
              className="inline-flex items-center justify-center rounded bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-sm font-semibold tracking-wide transition-colors"
            >
              Se alle ydelser
            </Link>
          </div>
        </div>
      </section>

      {/* Udvalgte referencer Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                Vores arbejde
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
                Udvalgte referencer
              </h2>
            </div>
            <Link
              to="/galleri"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-emerald-600 transition-colors group"
            >
              Se alle referencer
              <i className="fa-regular fa-arrow-right transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {selectedProjects.map((project, index) => (
              <div
                key={project.id}
                onClick={() => setLightboxIndex(index)}
                className={`group cursor-pointer flex flex-col bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden ${
                  index === 2 ? 'sm:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={project.image[0]}
                    alt={project.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <i className="fa-regular fa-magnifying-glass-plus text-lg" />
                    </div>
                  </div>
                  <span className="absolute top-4 left-4 text-xs font-bold text-white bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
                    {project.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                    {project.title}
                  </h3>
                  <p className="mt-2 text-slate-500 text-xs sm:text-sm line-clamp-2 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kunderne siger Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Anmeldelser
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
              Kunderne siger
            </h2>
            <p className="mt-4 text-slate-500 text-sm sm:text-base">
              Troværdighed og tilfredse kunder er vores vigtigste mål. Her kan du læse et udpluk af de anmeldelser, vi har modtaget fra glade boligejere.
            </p>
          </div>
        </div>

        {/* Infinite Slider */}
        <InfiniteSlider reviews={reviews} />

        <div className="text-center mt-10">
          <Link
            to="/kunderne-siger"
            className="inline-flex items-center justify-center rounded bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-sm font-semibold tracking-wide transition-colors"
          >
            Læs flere anmeldelser
          </Link>
        </div>
      </section>

      {/* Kontaktsektion */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-950 text-white rounded-2xl overflow-hidden shadow-xl border border-slate-800 relative">
            {/* Background elements */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.08),transparent_50%)]" />

            <div className="relative z-10 px-6 py-12 sm:p-16 lg:p-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
                  Kontakt os i dag
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                  Skal vi hjælpe dig med dit næste byggeprojekt?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Vi står altid klar til at tage en uforpligtende snak om dine drømme og ønsker. Kontakt os på telefon eller e-mail, eller udfyld vores kontaktformular, så vender vi hurtigt tilbage.
                </p>

                {/* Contact Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-slate-800">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Ring direkte</p>
                    <a
                      href="tel:+4560183562"
                      className="text-lg font-bold text-white hover:text-emerald-400 transition-colors mt-1 block whitespace-nowrap"
                    >
                      60 18 35 62
                    </a>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Send en e-mail</p>
                    <a
                      href="mailto:kontakt@kpheiffer.dk"
                      className="text-lg font-bold text-white hover:text-emerald-400 transition-colors mt-1 block whitespace-nowrap"
                    >
                      kontakt@kpheiffer.dk
                    </a>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-4 text-base font-bold tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-emerald-900/40 active:scale-95"
                >
                  Gå til kontaktformular
                  <i className="fa-regular fa-arrow-right ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for Gallery */}
      {lightboxIndex !== null && (
        <Lightbox
          images={selectedProjects}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev === 0 ? selectedProjects.length - 1 : prev - 1))}
          onNext={() => setLightboxIndex((prev) => (prev === selectedProjects.length - 1 ? 0 : prev + 1))}
        />
      )}
    </>
  );
}
